import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import noop from "lodash/noop";
import { memo, useCallback, useMemo, useRef } from "react";
import { useResizeDetector } from "react-resize-detector/build/withPolyfill";
import { AssetGalleryCardSize, useGalleryAssets } from "~/components/Gallery/useGalleryAssets";
import { BoardGalleryCardDesktopSize, BoardGalleryCardMobileSize, useGalleryBoards } from "~/components/Gallery/useGalleryBoards";
import { FileGalleryCardSize, useGalleryFiles } from "~/components/Gallery/useGalleryFiles";
import LoadMoreSpinner from "~/components/LoadMoreSpinner";
import { PublicBoardGalleryAsset } from "~/components/PublicBoard/PublicBoardGalleryAsset";
import { PublicBoardGalleryBoard } from "~/components/PublicBoard/PublicBoardGalleryBoard";
import { PublicBoardGalleryFile } from "~/components/PublicBoard/PublicBoardGalleryFile";
import { usePublicPermissions } from "~/components/PublicPermissionsProvider";
import { SelectableGalleryView } from "~/components/SelectableGalleryView/SelectableGalleryView";
import { ASSET_CARD_ROW_MT } from "~/constants/assetCard";
import { WKSPC_CLIP_INFO_HEIGHT, WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT } from "~/constants/WorkspaceSpacing";
import { useCardSizePreference } from "~/hooks/useCardSizePreference";
import { useGroupedGallerySelectedItems } from "~/hooks/useGroupedGallerySelectedItems";
import { useWorkspaceHorizontalPadding } from "~/hooks/useWorkspaceHorizontalPadding";
import { isTitleAndMetadataVisibleSelector, visibleCustomFieldsLengthSelector } from "~/store/configViews/selectors";
import { usePublicGalleryViewItems } from "~/swr-hooks/gallery/galleryView/usePublicGalleryViewItems";
import { canChangeAssetCustomFields } from "~/utils/permissions/assetPermissions";
import { canChangeBoardCustomFields } from "~/utils/permissions/boardPermissions";
import { canGenerateZip } from "~/utils/permissions/taskPermissions";
import { useAirSelector } from "~/utils/ReduxUtils";
import { PublicBoardNullState } from "./PublicBoardNullState";
const style = {
    paddingBottom: "16rem"
};
export const PublicBoardGalleryView = /*#__PURE__*/ memo((param)=>{
    let { scrollElementRef } = param;
    var _boardsData_items, _boardsData_items1;
    const containerRef = useRef(null);
    const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
    const { data: { boards: boardsData, clips: clipsData, files: filesData }, isLoading, loadNextPage, isEmpty } = usePublicGalleryViewItems();
    const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
    const visibleCustomFieldsLength = useAirSelector(visibleCustomFieldsLengthSelector);
    const { isAboveSmallScreen, isAboveMediumScreen } = useBreakpointsContext();
    const { cardSize } = useCardSizePreference();
    var _boardsData_items2, _clipsData_items, _filesData_items;
    useGroupedGallerySelectedItems((_boardsData_items2 = boardsData === null || boardsData === void 0 ? void 0 : boardsData.items) !== null && _boardsData_items2 !== void 0 ? _boardsData_items2 : [], (_clipsData_items = clipsData === null || clipsData === void 0 ? void 0 : clipsData.items) !== null && _clipsData_items !== void 0 ? _clipsData_items : [], (_filesData_items = filesData === null || filesData === void 0 ? void 0 : filesData.items) !== null && _filesData_items !== void 0 ? _filesData_items : []);
    const { width = 0, ref: widthRef } = useResizeDetector({
        handleHeight: false,
        refreshMode: "throttle",
        refreshRate: 300
    });
    const { permissions } = usePublicPermissions();
    const canDownload = canGenerateZip(permissions);
    const isClipSelectable = useCallback(()=>canChangeAssetCustomFields(permissions) || canDownload, [
        canDownload,
        permissions
    ]);
    const isBoardSelectable = useCallback(()=>canChangeBoardCustomFields(permissions) || canDownload, [
        canDownload,
        permissions
    ]);
    const boardsSection = useGalleryBoards({
        data: boardsData,
        containerWidth: width,
        renderBoard: (param)=>{
            let { data, index, box } = param;
            return /*#__PURE__*/ _jsx(PublicBoardGalleryBoard, {
                data: data,
                index: index,
                box: box
            });
        },
        loadMore: loadNextPage,
        containerHorizontalPadding: responsiveHorizontalPadding,
        isSelectable: isBoardSelectable,
        itemHeight: isAboveSmallScreen ? BoardGalleryCardDesktopSize[cardSize] : BoardGalleryCardMobileSize[cardSize]
    });
    const fileAdditionalHeight = useMemo(()=>{
        const additionalRows = Math.max(0, visibleCustomFieldsLength - 2);
        return additionalRows * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
    }, [
        visibleCustomFieldsLength
    ]);
    const assetAdditionalHeight = useMemo(()=>{
        let height = 0;
        if (isTitleAndMetadataVisible) height += WKSPC_CLIP_INFO_HEIGHT;
        if (visibleCustomFieldsLength) height += ASSET_CARD_ROW_MT;
        return height + visibleCustomFieldsLength * WKSPC_CUSTOM_FIELD_DISPLAY_HEIGHT;
    }, [
        visibleCustomFieldsLength,
        isTitleAndMetadataVisible
    ]);
    const assetsSection = useGalleryAssets({
        renderAsset: (param)=>{
            let { data, index, box } = param;
            return /*#__PURE__*/ _jsx(PublicBoardGalleryAsset, {
                data: data,
                index: index,
                box: box
            });
        },
        isFirstSection: !(boardsData === null || boardsData === void 0 ? void 0 : (_boardsData_items = boardsData.items) === null || _boardsData_items === void 0 ? void 0 : _boardsData_items.length),
        data: clipsData,
        containerWidth: width,
        loadMore: loadNextPage,
        containerHorizontalPadding: responsiveHorizontalPadding,
        itemAdditionalHeight: assetAdditionalHeight,
        isSelectable: isClipSelectable,
        itemHeight: AssetGalleryCardSize[cardSize]
    });
    const filesSection = useGalleryFiles({
        renderFile: (param)=>{
            let { data, index, box } = param;
            return /*#__PURE__*/ _jsx(PublicBoardGalleryFile, {
                data: data,
                index: index,
                box: box
            });
        },
        isFirstSection: !(boardsData === null || boardsData === void 0 ? void 0 : (_boardsData_items1 = boardsData.items) === null || _boardsData_items1 === void 0 ? void 0 : _boardsData_items1.length) && !(clipsData === null || clipsData === void 0 ? void 0 : clipsData.items.length),
        data: filesData,
        containerWidth: width,
        loadMore: loadNextPage,
        containerHorizontalPadding: responsiveHorizontalPadding,
        isSelectable: isClipSelectable,
        itemHeight: FileGalleryCardSize[cardSize] + fileAdditionalHeight,
        itemWidth: !isAboveMediumScreen ? 300 : undefined,
        itemHorizontalSpacing: !isAboveSmallScreen ? 0 : 24
    });
    const sections = [
        boardsSection,
        assetsSection,
        filesSection
    ].filter((section)=>!!section);
    return /*#__PURE__*/ _jsxs("div", {
        className: "mx-auto w-full",
        children: [
            /*#__PURE__*/ _jsx("div", {
                ref: widthRef
            }),
            isLoading || typeof document === "undefined" ? /*#__PURE__*/ _jsx(LoadMoreSpinner, {
                isVisible: true,
                isLoading: isLoading,
                loadMore: noop
            }) : isEmpty ? /*#__PURE__*/ _jsx(PublicBoardNullState, {}) : width ? /*#__PURE__*/ _jsx("div", {
                className: "relative mt-6 min-h-full",
                ref: containerRef,
                children: /*#__PURE__*/ _jsx(SelectableGalleryView, {
                    scrollElementRef: scrollElementRef,
                    containerRef: containerRef,
                    sections: sections,
                    width: width,
                    style: style
                })
            }) : null
        ]
    });
});
PublicBoardGalleryView.displayName = "PublicBoardGalleryView";
